import { publicConfig } from "@/lib/public-config";

import { ApiError } from "./api-error";
import { getValidAccessToken } from "./auth";

type EdgeCallAuth = "authenticated" | "public";

export interface EdgeCallOptions {
  method?: string;
  body?: Record<string, unknown>;
  params?: Record<string, string>;
}

interface InternalEdgeCallOptions extends EdgeCallOptions {
  auth?: EdgeCallAuth;
}

const SUPABASE_URL = publicConfig.supabaseUrl;
const PROJECT_ID = publicConfig.supabaseProjectId;
const SUPABASE_PUBLISHABLE_KEY = publicConfig.supabasePublishableKey;

function resolveFunctionsBaseUrl(): string {
  if (SUPABASE_URL) {
    return new URL("/functions/v1/", SUPABASE_URL).toString();
  }

  if (PROJECT_ID) {
    return `https://${PROJECT_ID}.supabase.co/functions/v1/`;
  }

  throw new ApiError("missing_supabase_url");
}

const FUNCTIONS_BASE_URL = resolveFunctionsBaseUrl();

async function parseFunctionResponse(res: Response): Promise<Record<string, unknown>> {
  const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  if (!res.ok) {
    const code = typeof payload.code === "string"
      ? payload.code
      : typeof payload.error === "string"
        ? payload.error
        : "request_failed";
    const message = typeof payload.message === "string"
      ? payload.message
      : typeof payload.details === "string"
        ? payload.details
        : code;

    throw new ApiError(code, message);
  }

  return payload;
}

async function callEdge(
  functionName: string,
  action: string,
  options: InternalEdgeCallOptions = {},
): Promise<Record<string, unknown>> {
  const { method = "POST", body, params, auth = "authenticated" } = options;
  const url = new URL(functionName, FUNCTIONS_BASE_URL);
  url.searchParams.set("action", action);

  if (params) {
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  }

  const buildHeaders = (accessToken?: string): Record<string, string> => ({
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(body ? { "Content-Type": "application/json" } : {}),
    ...(SUPABASE_PUBLISHABLE_KEY ? { apikey: SUPABASE_PUBLISHABLE_KEY } : {}),
  });

  const runRequest = (accessToken?: string) =>
    fetch(url.toString(), {
      method,
      headers: buildHeaders(accessToken),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

  if (auth === "public") {
    return parseFunctionResponse(await runRequest());
  }

  let accessToken = await getValidAccessToken();
  let response = await runRequest(accessToken);

  if (response.status === 401) {
    accessToken = await getValidAccessToken();
    response = await runRequest(accessToken);

    if (response.status === 401) {
      throw new ApiError("not_authenticated");
    }
  }

  return parseFunctionResponse(response);
}

export async function callEdgeFunction(
  functionName: string,
  action: string,
  options: EdgeCallOptions = {},
): Promise<Record<string, unknown>> {
  return callEdge(functionName, action, { ...options, auth: "authenticated" });
}

export async function callEdgeData<T>(
  functionName: string,
  action: string,
  options: EdgeCallOptions = {},
): Promise<T> {
  const payload = await callEdgeFunction(functionName, action, options);
  return (payload.data ?? payload) as T;
}

export async function callPublicEdgeFunction(
  functionName: string,
  action: string,
  options: EdgeCallOptions = {},
): Promise<Record<string, unknown>> {
  return callEdge(functionName, action, { ...options, auth: "public" });
}

export async function callPublicEdgeData<T>(
  functionName: string,
  action: string,
  options: EdgeCallOptions = {},
): Promise<T> {
  const payload = await callPublicEdgeFunction(functionName, action, options);
  return (payload.data ?? payload) as T;
}
